import { useMemo } from 'react'
import { usePaymentAssignmentUsersQuery } from '../../../api/finance-api-client'
import type {
  PaymentAssignmentUserDto,
  PaymentAssignmentUserRole,
} from '../../../api/dto/get-payment-assignment-users.dto'
import { PaymentAssignmentRole } from '../Utils/roles'

/**
 * Primary role to fetch users for.
 * CHECKER users are always appended so checkers can be assigned to any role slot.
 */
export type UserOptionsPrimaryRole =
  | PaymentAssignmentRole.MAKER
  | PaymentAssignmentRole.ESCALATOR

/** Display-ready user option for selects and filter dropdowns. */
export type PaymentAssignmentUserOption = Pick<
  PaymentAssignmentUserDto,
  'id' | 'name' | 'role' | 'userId' | 'email'
>

const ROLE_FETCH_MAP: Record<
  UserOptionsPrimaryRole,
  PaymentAssignmentUserRole[]
> = {
  MAKER: [PaymentAssignmentRole.MAKER, PaymentAssignmentRole.CHECKER],
  ESCALATOR: [PaymentAssignmentRole.ESCALATOR, PaymentAssignmentRole.CHECKER],
}

const ROLE_SORT_ORDER: Record<UserOptionsPrimaryRole, string[]> = {
  MAKER: [PaymentAssignmentRole.MAKER, PaymentAssignmentRole.CHECKER],
  ESCALATOR: [PaymentAssignmentRole.ESCALATOR, PaymentAssignmentRole.CHECKER],
}

/**
 * Sorts users by role priority (roleOrder index) then alphabetically by name.
 * Users whose role is not in roleOrder are placed last.
 */
export function sortUsersByRoleThenName<
  T extends { role?: string; name: string },
>(users: T[], roleOrder: string[]): T[] {
  return [...users].sort((a, b) => {
    const aIdx = roleOrder.indexOf(a.role ?? '')
    const bIdx = roleOrder.indexOf(b.role ?? '')
    const aOrder = aIdx === -1 ? roleOrder.length : aIdx
    const bOrder = bIdx === -1 ? roleOrder.length : bIdx
    if (aOrder !== bOrder) return aOrder - bOrder
    return a.name.localeCompare(b.name)
  })
}

interface UsePaymentAssignmentUserOptionsResult {
  options: PaymentAssignmentUserOption[]
  isLoading: boolean
  /** Raw items from the API (unsorted), useful for lookup by id/userId. */
  items: PaymentAssignmentUserDto[]
}

/**
 * Fetches users for a given primary role slot (MAKER or ESCALATOR), always
 * including CHECKER users. Returns options sorted by role (primary first,
 * then CHECKER) then alphabetically by name.
 */
export function usePaymentAssignmentUserOptions(
  primaryRole: UserOptionsPrimaryRole,
  queryOptions?: { enabled?: boolean }
): UsePaymentAssignmentUserOptionsResult {
  const roles = ROLE_FETCH_MAP[primaryRole]
  const { data, isLoading } = usePaymentAssignmentUsersQuery(
    { roles },
    { enabled: queryOptions?.enabled ?? true }
  )

  const items = data?.items ?? []

  const options = useMemo(
    () => sortUsersByRoleThenName(items, ROLE_SORT_ORDER[primaryRole]),
    [items, primaryRole]
  )

  return { options, isLoading, items }
}
